import React from 'react';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  Alert,
  SimpleGrid,
  Stat,
  Progress,
  Table,
  ButtonGroup,
  Menu,
  Portal,
} from '@chakra-ui/react';
import { Thead, Tbody, Tr, Th, Td } from '@/components/ui/chakra-v2-compat';
import { FaThList, FaChartPie, FaFilePdf, FaMagic } from 'react-icons/fa';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
} from 'recharts';
import {
  MotionBox,
  LABEL_ESTADO_ENSAYO,
  LABEL_ESTADO_ASIS,
  LABEL_ESTADO_ACTUACION,
  LABEL_AVAL_STATUS,
  MiniDistribucionAsist,
  formatPct,
  nombreMusico,
  LABEL_DELTA_KPI,
  formatDeltaLine,
  HEX_EST_ENSAYO,
  HEX_EST_ASIS,
  HEX_EST_ACT,
  BORDE_EST_ENSAYO,
  BORDE_EST_ACT,
  HEX_AVAL,
  toPieData,
  toPieDataWithKeyColors,
  RenderPie,
  barDataFromObras,
  barDataFromAutores,
  barDataSedesGrupos,
  BarHorizMini,
  ListDistribAnimada,
  SeccionCaja,
  COL_ASIS,
} from './metricas-informe-parts.jsx';

export const MOTIVO_TEXTO = {
  parametros_incompletos: 'Faltan datos o el modo no es válido.',
  anio_en_curso_o_futuro_no_permitido:
    'Solo se pueden consultar años civiles completos ya cerrados (no el año en curso ni futuros).',
  mes_futuro_no_permitido: 'No se puede elegir un mes posterior al actual.',
  rango_solicitado_invertido: '«Desde» no puede ser posterior a «Hasta».',
  sin_solape_con_vida_banda: 'El periodo no solapa con la actividad desde el alta de la banda.',
  hasta_solicitado_anterior_a_desde:
    'Tras ajustar por el día de alta y por hoy, el rango solicitado queda vacío.',
};

export function ymdLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export const MESES_ES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

export function formatYmdEs(ymd) {
  if (!ymd || typeof ymd !== 'string') return '—';
  const p = ymd.split('-');
  if (p.length !== 3) return ymd;
  const y = parseInt(p[0], 10);
  const m = parseInt(p[1], 10);
  const d = parseInt(p[2], 10);
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return ymd;
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** @returns {{ linea1: string, linea2: string | null }} */
export function tituloPeriodoDesdeModo(modoSnap, data, t) {
  if (!data) {
    return { linea1: t.informe.tituloRango, linea2: t.informe.sinRango };
  }
  const d1 = data.desdeYmd ? formatYmdEs(data.desdeYmd) : null;
  const d2 = data.hastaYmd ? formatYmdEs(data.hastaYmd) : null;
  if (modoSnap?.tipo === 'anio' && typeof modoSnap.anio === 'number') {
    return { linea1: t.informe.tituloAnio(modoSnap.anio), linea2: null };
  }
  if (modoSnap?.tipo === 'mes' && modoSnap.mes && modoSnap.anioMes) {
    const mnom = MESES_ES[(Number(modoSnap.mes) || 1) - 1] || '';
    return {
      linea1: t.informe.tituloMes(mnom, modoSnap.anioMes),
      linea2: null,
    };
  }
  if (d1 && d2) {
    return { linea1: t.informe.tituloRango, linea2: t.informe.entreFechas(d1, d2) };
  }
  return { linea1: t.informe.tituloRango, linea2: null };
}


/** Título principal del periodo (mismo bloque que en la pantalla Estadísticas). */
export function MetricasInformeHero({
  data,
  modoInforme,
  tMetricas,
  sub,
  informeHeroBorder,
  informeHeroBg,
}) {
  if (!data) return null;
  const { linea1, linea2 } = tituloPeriodoDesdeModo(modoInforme, data, tMetricas);
  const advertir = data.vacio || !data.ok;
  const motivo = data?.motivoRechazo;
  const mensajeRechazo = motivo && MOTIVO_TEXTO[motivo] ? MOTIVO_TEXTO[motivo] : null;
  return (
    <MotionBox
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
    >
      <Box
        borderRadius="2xl"
        overflow="hidden"
        borderWidth="1px"
        borderColor={informeHeroBorder}
        bg={informeHeroBg}
        boxShadow="0 12px 40px -12px rgba(103, 65, 188, 0.28)"
      >
        <Box h="4px" bgGradient="linear(to-r, purple.500, purple.400, cyan.400)" aria-hidden />
        <Box px={{ base: 5, md: 8 }} py={{ base: 6, md: 8 }}>
          <Heading
            as="h2"
            size="lg"
            fontWeight="800"
            letterSpacing="-0.02em"
            lineHeight="1.2"
            bgGradient="linear(to-r, purple.600, purple.500, cyan.600)"
            bgClip="text"
            color="transparent"
          >
            {linea1}
          </Heading>
          {linea2 ? (
            <Text fontSize={{ base: 'md', md: 'lg' }} color={sub} fontWeight="600" mt={2}>
              {linea2}
            </Text>
          ) : null}
          {advertir ? (
            <VStack align="flex-start" gap={2} mt={5}>
              <Text color="orange.500" fontWeight="600">
                {tMetricas.informe.noDatosRango}
              </Text>
              {motivo ? (
                <Text fontSize="sm" color={sub}>
                  {mensajeRechazo || `Motivo: ${motivo}`}
                </Text>
              ) : null}
            </VStack>
          ) : null}
        </Box>
      </Box>
    </MotionBox>
  );
}


/** Cuerpo completo del informe (mismo orden y datos que en Estadísticas). */
export function MetricasInformeContenido({
  data,
  tMetricas,
  sub,
  cardBg,
  vistaGraficos,
  setVistaGraficos,
  showToolbar = true,
  pdfGenerando = false,
  exportarMetricasPdf = () => {},
  abrirResumenAnimado = () => {},
  resumenAnimadoLoading = false,
  incluirNarrativa,
}) {
  return (
    <>
      {data && data.resumen && data.ok && !data.vacio && (
        <MotionBox
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.05, ease: [0.4, 0, 0.2, 1] }}
        >
          <Card.Root bg={cardBg} shadow="lg" borderWidth="1px" borderRadius="2xl" overflow="hidden">
            <Box h="3px" bgGradient="linear(to-r, purple.500, cyan.400)" aria-hidden />
            <Card.Header pb={0} pt={5} px={{ base: 4, md: 6 }}>
              <Flex justify="space-between" align="flex-start" flexWrap="wrap" gap={3}>
                <Box flex="1" minW="200px">
                  <Text fontWeight="800" fontSize="lg" letterSpacing="-0.02em">
                    {tMetricas.informe.resumenSeccion}
                  </Text>
                  <Text fontSize="sm" color={sub} fontWeight="normal" mt={1} maxW="2xl" lineHeight="tall">
                    {tMetricas.informe.resumenSeccionSub}
                  </Text>
                </Box>
                {showToolbar ? (
                <HStack flexWrap="wrap" justify="flex-end" gap={2}>
                  <ButtonGroup size="sm" attached>
                    <Button
                      colorPalette="purple"
                      variant={!vistaGraficos ? 'solid' : 'outline'}
                      onClick={() => setVistaGraficos(false)}><FaThList />{tMetricas.informe.vistaDetalle}</Button>
                    <Button
                      colorPalette="purple"
                      variant={vistaGraficos ? 'solid' : 'outline'}
                      onClick={() => setVistaGraficos(true)}><FaChartPie />{tMetricas.informe.vistaGraficos}</Button>
                  </ButtonGroup>
                  <Menu.Root>
                    <Menu.Trigger asChild><Button
                        size="sm"
                        variant="outline"
                        loading={pdfGenerando}
                        loadingText={tMetricas.informe.pdfGenerando}><FaFilePdf />
                        {tMetricas.informe.pdfDescargar}
                      </Button></Menu.Trigger>
                    <Portal><Menu.Positioner><Menu.Content>
                          <Menu.Item
                            icon={<FaThList />}
                            onSelect={() => exportarMetricasPdf(false)}
                            value='item-0'>
                            {tMetricas.informe.pdfMenuDetalle}
                          </Menu.Item>
                          <Menu.Item
                            icon={<FaChartPie />}
                            onSelect={() => exportarMetricasPdf(true)}
                            value='item-1'>
                            {tMetricas.informe.pdfMenuGraficos}
                          </Menu.Item>
                        </Menu.Content></Menu.Positioner></Portal>
                  </Menu.Root>
                  <Button
                    size="sm"
                    variant="solid"
                    colorPalette="purple"
                    onClick={abrirResumenAnimado}
                    loading={resumenAnimadoLoading}
                    title={tMetricas.wrapped.ctaHelp}><FaMagic />{tMetricas.wrapped.cta}</Button>
                </HStack>
                ) : null}
              </Flex>
            </Card.Header>
            <Card.Body px={{ base: 4, md: 6 }} pb={6}>
            {incluirNarrativa && (
              <MotionBox
                initial={{ opacity: 0, scale: 0.99 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35 }}
              >
                <Alert.Root
                  status={data.narrativaIa ? 'info' : 'warning'}
                  borderRadius="xl"
                  mb={4}
                  borderLeftWidth="4px"
                  borderLeftColor={data.narrativaIa ? 'cyan.400' : 'orange.400'}
                >
                  <Alert.Indicator />
                  <Box>
                    <Text fontWeight="700" fontSize="sm" mb={data.narrativaIa ? 1 : 0}>
                      {data.narrativaIa ? 'Lectura del periodo' : 'Narrativa con IA no disponible'}
                    </Text>
                    {data.narrativaIa ? (
                      <Text fontSize="sm" color={sub} whiteSpace="pre-line">
                        {data.narrativaIa}
                      </Text>
                    ) : (
                      <Text fontSize="sm" color={sub}>
                        No se ha generado texto. Puede faltar la clave de la API de IA en el servidor, o
                        haber ocurrido un error temporal. Vuelve a consultar las estadísticas o pide
                        ayuda a administración.
                      </Text>
                    )}
                  </Box>
                </Alert.Root>
              </MotionBox>
            )}
            <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
              <SeccionCaja titulo="Ensayos" tituloColor="purple.500">
                <Stat.Root size="sm">
                  <Stat.Label>Total con fecha en el rango</Stat.Label>
                  <Stat.ValueText>{data.resumen.ensayos.total}</Stat.ValueText>
                </Stat.Root>
                <Stat.Root size="sm" mt={2}>
                  <Stat.Label>Horas (sólo con hora de fin)</Stat.Label>
                  <Stat.ValueText>
                    {data.resumen.ensayos.horasSuma} h
                  </Stat.ValueText>
                  {data.resumen.ensayos.ensayosSinHoraFin > 0 && (
                    <Stat.HelpText>
                      {data.resumen.ensayos.ensayosSinHoraFin} ensayo(s) sin hora de fin (no suman)
                    </Stat.HelpText>
                  )}
                </Stat.Root>
                <Text fontSize="xs" color={sub} mt={3} fontWeight="700" textTransform="uppercase" letterSpacing="0.05em">
                  Por estado
                </Text>
                {vistaGraficos ? (
                  <RenderPie
                    data={toPieDataWithKeyColors(
                      data.resumen.ensayos.porEstado,
                      LABEL_ESTADO_ENSAYO,
                      HEX_EST_ENSAYO,
                    )}
                    height={210}
                  />
                ) : (
                  <ListDistribAnimada
                    record={data.resumen.ensayos.porEstado}
                    labelMap={LABEL_ESTADO_ENSAYO}
                    colorOfKey={(k) => BORDE_EST_ENSAYO[k] || 'purple.400'}
                    muted={sub}
                  />
                )}
              </SeccionCaja>
              <SeccionCaja titulo="Asistencia a ensayos" tituloColor="green.500">
                <Stat.Root size="sm">
                  <Stat.Label>Registros (músico × ensayo)</Stat.Label>
                  <Stat.ValueText>{data.resumen.asistenciaEnsayos.totalRegistros}</Stat.ValueText>
                  {data.resumen.asistenciaEnsayos.tasaPresenteSobreCerrado != null && (
                    <Stat.HelpText>
                      {formatPct(data.resumen.asistenciaEnsayos.tasaPresenteSobreCerrado)} presente
                      sobre cerrado (presente / presente+ausente+justificado)
                    </Stat.HelpText>
                  )}
                </Stat.Root>
                {data.resumen.asistenciaEnsayos.tasaPresenteSobreCerrado != null && (
                  <Progress.Root
                    value={data.resumen.asistenciaEnsayos.tasaPresenteSobreCerrado * 100}
                    size="sm"
                    colorPalette="green"
                    borderRadius="md"
                    mt={1}
                    maxW="100%">
                    <Progress.Track>
                      <Progress.Range />
                    </Progress.Track>
                  </Progress.Root>
                )}
                {!vistaGraficos && (
                  <>
                    <Text fontSize="xs" color={sub} mt={2} fontWeight="600">
                      Distribución
                    </Text>
                    <MiniDistribucionAsist porEstado={data.resumen.asistenciaEnsayos.porEstado} />
                  </>
                )}
                <Text fontSize="xs" color={sub} mt={3} fontWeight="700" textTransform="uppercase" letterSpacing="0.05em">
                  Por estado
                </Text>
                {vistaGraficos ? (
                  <RenderPie
                    data={toPieDataWithKeyColors(
                      data.resumen.asistenciaEnsayos.porEstado,
                      LABEL_ESTADO_ASIS,
                      HEX_EST_ASIS,
                    )}
                    height={220}
                  />
                ) : (
                  <ListDistribAnimada
                    record={data.resumen.asistenciaEnsayos.porEstado}
                    labelMap={LABEL_ESTADO_ASIS}
                    colorOfKey={(k) => COL_ASIS[k] || 'gray.400'}
                    muted={sub}
                  />
                )}
              </SeccionCaja>
              <SeccionCaja titulo="Actuaciones" tituloColor="blue.500">
                <Stat.Root size="sm">
                  <Stat.Label>Total con fecha en el rango</Stat.Label>
                  <Stat.ValueText>{data.resumen.actuaciones.total}</Stat.ValueText>
                </Stat.Root>
                <Text fontSize="xs" color={sub} mt={3} fontWeight="700" textTransform="uppercase" letterSpacing="0.05em">
                  Por estado
                </Text>
                {vistaGraficos ? (
                  <RenderPie
                    data={toPieDataWithKeyColors(
                      data.resumen.actuaciones.porEstado,
                      LABEL_ESTADO_ACTUACION,
                      HEX_EST_ACT,
                    )}
                    height={240}
                  />
                ) : (
                  <ListDistribAnimada
                    record={data.resumen.actuaciones.porEstado}
                    labelMap={LABEL_ESTADO_ACTUACION}
                    colorOfKey={(k) => BORDE_EST_ACT[k] || 'blue.400'}
                    muted={sub}
                  />
                )}
              </SeccionCaja>
              {data.resumen.repertorioActuaciones && (
                <SeccionCaja
                  gridColumn={{ base: '1', md: '1 / -1' }}
                  titulo="Repertorio en actuaciones (programas)"
                  tituloColor="blue.700"
                  descripcion="Cada obra en un programa cuenta una vez. Marchas con datos del catálogo."
                  muted={sub}
                >
                  <Text fontSize="xs" color={sub} fontWeight="600" mb={3}>
                    Actuaciones con al menos una obra:{' '}
                    {data.resumen.repertorioActuaciones.actuacionesAnalizadas}
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                    <Box>
                      <Text fontSize="sm" fontWeight="700" mb={2} color="blue.600">
                        Obras más en programas
                      </Text>
                      {(data.resumen.repertorioActuaciones.obras || []).length > 0 ? (
                        vistaGraficos ? (
                          <BarHorizMini
                            data={barDataFromObras(data.resumen.repertorioActuaciones.obras)}
                            h={240}
                          />
                        ) : (
                          <Table.Root size="sm" variant="striped" colorPalette="purple" borderRadius="lg" overflow="hidden">
                            <Table.Header bg="blackAlpha.50" _dark={{ bg: 'whiteAlpha.100' }}>
                              <Table.Row>
                                <Table.ColumnHeader>Obra</Table.ColumnHeader>
                                <Table.ColumnHeader>Autor</Table.ColumnHeader>
                                <Table.ColumnHeader textAlign='end'>Apar.</Table.ColumnHeader>
                              </Table.Row>
                            </Table.Header>
                            <Table.Body>
                              {data.resumen.repertorioActuaciones.obras.map((o) => (
                                <Table.Row
                                  key={o.clave}
                                  transition="background 0.2s"
                                  _hover={{ bg: 'purple.50' }}
                                  _dark={{ _hover: { bg: 'whiteAlpha.100' } }}
                                >
                                  <Table.Cell fontWeight="500">{o.titulo}</Table.Cell>
                                  <Table.Cell fontSize="sm" color={sub}>
                                    {o.autor ?? '—'}
                                  </Table.Cell>
                                  <Table.Cell fontWeight="700" textAlign='end'>
                                    {o.apariciones}
                                  </Table.Cell>
                                </Table.Row>
                                ))}
                            </Table.Body>
                          </Table.Root>
                        )
                      ) : (
                        <Text fontSize="sm" color={sub}>
                          Sin obras en repertorios de actuaciones en este periodo.
                        </Text>
                      )}
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="700" mb={2} color="blue.600">
                        Autores más interpretados
                      </Text>
                      {(data.resumen.repertorioActuaciones.autores || []).length > 0 ? (
                        vistaGraficos ? (
                          <BarHorizMini
                            data={barDataFromAutores(data.resumen.repertorioActuaciones.autores)}
                            h={220}
                          />
                        ) : (
                          <Table.Root size="sm" variant="striped" colorPalette="purple" borderRadius="lg" overflow="hidden">
                            <Table.Header bg="blackAlpha.50" _dark={{ bg: 'whiteAlpha.100' }}>
                              <Table.Row>
                                <Table.ColumnHeader>Autor</Table.ColumnHeader>
                                <Table.ColumnHeader textAlign='end'>Apar. en obras</Table.ColumnHeader>
                              </Table.Row>
                            </Table.Header>
                            <Table.Body>
                              {data.resumen.repertorioActuaciones.autores.map((a, idx) => (
                                <Table.Row
                                  key={`${a.autor}-${idx}`}
                                  transition="background 0.2s"
                                  _hover={{ bg: 'purple.50' }}
                                  _dark={{ _hover: { bg: 'whiteAlpha.100' } }}
                                >
                                  <Table.Cell fontWeight="500">{a.autor}</Table.Cell>
                                  <Table.Cell fontWeight="700" textAlign='end'>
                                    {a.apariciones}
                                  </Table.Cell>
                                </Table.Row>
                                ))}
                            </Table.Body>
                          </Table.Root>
                        )
                      ) : (
                        <Text fontSize="sm" color={sub}>
                          Sin autores (sin autor en obras o solo marchas sin autor en catálogo).
                        </Text>
                      )}
                    </Box>
                  </SimpleGrid>
                </SeccionCaja>
              )}
              {data.resumen.encuestasAsistenciaActuaciones && (
                <SeccionCaja titulo="Encuestas (asistencia a actuación)" tituloColor="cyan.600">
                  <Text fontSize="xs" color={sub} fontWeight="600" mb={2}>
                    Actuaciones con encuesta en el rango:{' '}
                    {data.resumen.encuestasAsistenciaActuaciones.actuacionesConEncuesta}
                  </Text>
                  <Stat.Root size="sm">
                    <Stat.Label>Respuestas en el periodo</Stat.Label>
                    <Stat.ValueText>{data.resumen.encuestasAsistenciaActuaciones.totalRespuestas}</Stat.ValueText>
                    {data.resumen.encuestasAsistenciaActuaciones.tasaSobreRespuestaBinaria != null && (
                      <Stat.HelpText>
                        {formatPct(data.resumen.encuestasAsistenciaActuaciones.tasaSobreRespuestaBinaria)} asiste
                        (sí / sí+no)
                      </Stat.HelpText>
                    )}
                  </Stat.Root>
                  {vistaGraficos ? (
                    <RenderPie
                      data={toPieData(
                        {
                          'Sí': data.resumen.encuestasAsistenciaActuaciones.asisteSi,
                          'No': data.resumen.encuestasAsistenciaActuaciones.asisteNo,
                          'Sin contestar': data.resumen.encuestasAsistenciaActuaciones.sinContestar,
                        },
                        { 'Sí': 'Sí', 'No': 'No', 'Sin contestar': 'Sin contestar' },
                      )}
                      height={200}
                    />
                  ) : (
                    <ListDistribAnimada
                      record={{
                        'Sí': data.resumen.encuestasAsistenciaActuaciones.asisteSi,
                        'No': data.resumen.encuestasAsistenciaActuaciones.asisteNo,
                        'Sin contestar': data.resumen.encuestasAsistenciaActuaciones.sinContestar,
                      }}
                      labelMap={{ 'Sí': 'Sí', 'No': 'No', 'Sin contestar': 'Sin contestar' }}
                      colorOfKey={(k) =>
                        k === 'Sí' ? 'green.400' : k === 'No' ? 'red.400' : 'gray.400'
                      }
                      muted={sub}
                    />
                  )}
                  {data.resumen.pulidoFase7?.encuestasTasaRespuesta && (
                    <Text fontSize="sm" color={sub} mt={2}>
                      {tMetricas.fase7.tasaRespuestaTitle}:{' '}
                      {data.resumen.pulidoFase7.encuestasTasaRespuesta.tasa != null
                        ? formatPct(data.resumen.pulidoFase7.encuestasTasaRespuesta.tasa)
                        : '—'}
                      <Text as="span" fontSize="xs" display="block" mt={1}>
                        {tMetricas.fase7.tasaRespuestaHelp} ·{' '}
                        {data.resumen.pulidoFase7.encuestasTasaRespuesta.conRespuesta} /{' '}
                        {data.resumen.pulidoFase7.encuestasTasaRespuesta.invitaciones}
                      </Text>
                    </Text>
                  )}
                </SeccionCaja>
              )}
              {data.resumen.archivoObrasPartituras && (
                <SeccionCaja titulo="Archivo de partituras" tituloColor="pink.500">
                  <Text fontSize="md" fontWeight="600">
                    Obras creadas en el periodo:{' '}
                    <Text as="span" color="purple.500" fontSize="2xl" fontWeight="800">
                      {data.resumen.archivoObrasPartituras.obrasCreadas}
                    </Text>
                  </Text>
                  <Text fontSize="md" fontWeight="600" mt={2}>
                    Páginas añadidas en el periodo:{' '}
                    <Text as="span" color="pink.500" fontSize="2xl" fontWeight="800">
                      {data.resumen.archivoObrasPartituras.paginasCreadas}
                    </Text>
                  </Text>
                  <Text fontSize="xs" color={sub} mt={2}>
                    Por fecha de creación en el sistema (no por fecha de interpretación).
                  </Text>
                </SeccionCaja>
              )}
              {data.resumen.plantillaEnsayos && (
                <SeccionCaja titulo="Plantilla en ensayos" tituloColor="orange.500">
                  <Text fontSize="sm" fontWeight="600">
                    Músicos activos: {data.resumen.plantillaEnsayos.musicosActivos}
                  </Text>
                  <Text fontSize="sm" fontWeight="600" mt={2}>
                    Con al menos un «Presente» en el rango:{' '}
                    {data.resumen.plantillaEnsayos.musicosConPresencia}
                  </Text>
                  {data.resumen.plantillaEnsayos.ratioPresencia != null && (
                    <>
                      <Progress.Root
                        value={data.resumen.plantillaEnsayos.ratioPresencia * 100}
                        size="sm"
                        colorPalette="orange"
                        borderRadius="md"
                        mt={3}
                        maxW="100%"
                        striped
                        animated>
                        <Progress.Track>
                          <Progress.Range />
                        </Progress.Track>
                      </Progress.Root>
                      <Text fontSize="xs" color={sub} mt={1}>
                        {formatPct(data.resumen.plantillaEnsayos.ratioPresencia)} de la plantilla con presencia
                        registrada al menos un día
                      </Text>
                    </>
                  )}
                </SeccionCaja>
              )}
            </SimpleGrid>
          </Card.Body>
        </Card.Root>
        </MotionBox>
      )}

      {data && data.resumen?.pulidoFase7 && data.ok && !data.vacio && (
        <MotionBox
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.08, ease: [0.4, 0, 0.2, 1] }}
        >
          <Card.Root bg={cardBg} shadow="lg" borderWidth="1px" borderRadius="2xl" overflow="hidden">
            <Box h="3px" bgGradient="linear(to-r, teal.400, purple.500)" aria-hidden />
            <Card.Header pb={0} px={{ base: 4, md: 6 }} pt={5}>
              <Text fontWeight="800" fontSize="lg" letterSpacing="-0.02em">
                {tMetricas.fase7.cardTitle}
              </Text>
              <Text fontSize="sm" color={sub} fontWeight="normal" mt={1} lineHeight="tall">
                {tMetricas.fase7.cardSubtitle}
              </Text>
            </Card.Header>
            <Card.Body px={{ base: 4, md: 6 }} pb={6}>
              <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
              <SeccionCaja titulo={tMetricas.fase7.sedesTitle} tituloColor="teal.600">
                {(data.resumen.pulidoFase7.sedesMasUsadas || []).length > 0 ? (
                  vistaGraficos ? (
                    <BarHorizMini
                      data={barDataSedesGrupos(data.resumen.pulidoFase7.sedesMasUsadas)}
                      h={Math.min(56 + data.resumen.pulidoFase7.sedesMasUsadas.length * 28, 300)}
                    />
                  ) : (
                    <Table.Root size="sm" variant="striped" colorPalette="teal" borderRadius="lg" overflow="hidden">
                      <Table.Header bg="blackAlpha.50" _dark={{ bg: 'whiteAlpha.100' }}>
                        <Table.Row>
                          <Table.ColumnHeader>Sede</Table.ColumnHeader>
                          <Table.ColumnHeader textAlign='end'>Ensayos</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {data.resumen.pulidoFase7.sedesMasUsadas.map((s) => (
                          <Table.Row
                            key={s.id}
                            _hover={{ bg: 'teal.50' }}
                            _dark={{ _hover: { bg: 'whiteAlpha.100' } }}
                          >
                            <Table.Cell fontWeight="500">{s.nombre}</Table.Cell>
                            <Table.Cell fontWeight="700" textAlign='end'>
                              {s.ensayos}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  )
                ) : (
                  <Text fontSize="sm" color={sub}>
                    {tMetricas.fase7.emptySedes}
                  </Text>
                )}
              </SeccionCaja>
              <SeccionCaja titulo={tMetricas.fase7.gruposTitle} tituloColor="purple.600">
                {(data.resumen.pulidoFase7.gruposMasUsados || []).length > 0 ? (
                  vistaGraficos ? (
                    <BarHorizMini
                      data={barDataSedesGrupos(data.resumen.pulidoFase7.gruposMasUsados)}
                      h={Math.min(56 + data.resumen.pulidoFase7.gruposMasUsados.length * 28, 300)}
                    />
                  ) : (
                    <Table.Root size="sm" variant="striped" colorPalette="purple" borderRadius="lg" overflow="hidden">
                      <Table.Header bg="blackAlpha.50" _dark={{ bg: 'whiteAlpha.100' }}>
                        <Table.Row>
                          <Table.ColumnHeader>Grupo</Table.ColumnHeader>
                          <Table.ColumnHeader textAlign='end'>Ensayos</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {data.resumen.pulidoFase7.gruposMasUsados.map((g) => (
                          <Table.Row
                            key={g.id}
                            _hover={{ bg: 'purple.50' }}
                            _dark={{ _hover: { bg: 'whiteAlpha.100' } }}
                          >
                            <Table.Cell fontWeight="500">{g.nombre}</Table.Cell>
                            <Table.Cell fontWeight="700" textAlign='end'>
                              {g.ensayos}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  )
                ) : (
                  <Text fontSize="sm" color={sub}>
                    {tMetricas.fase7.emptyGrupos}
                  </Text>
                )}
              </SeccionCaja>
            </SimpleGrid>
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} mt={4}>
              <SeccionCaja titulo={tMetricas.fase7.avalesTitle} tituloColor="gray.700">
                {data.resumen.pulidoFase7.avalesCreadosEnRango.creadosEnRango > 0 ? (
                  <>
                    <Text fontSize="sm" fontWeight="700" mb={2}>
                      Total: {data.resumen.pulidoFase7.avalesCreadosEnRango.creadosEnRango}
                    </Text>
                    {vistaGraficos ? (
                      <RenderPie
                        data={toPieDataWithKeyColors(
                          data.resumen.pulidoFase7.avalesCreadosEnRango.porStatus || {},
                          LABEL_AVAL_STATUS,
                          HEX_AVAL,
                        )}
                        height={200}
                      />
                    ) : (
                      <ListDistribAnimada
                        record={data.resumen.pulidoFase7.avalesCreadosEnRango.porStatus || {}}
                        labelMap={LABEL_AVAL_STATUS}
                        colorOfKey={() => 'purple.400'}
                        muted={sub}
                      />
                    )}
                  </>
                ) : (
                  <Text fontSize="sm" color={sub}>
                    {tMetricas.fase7.avalesEmpty}
                  </Text>
                )}
              </SeccionCaja>
              <SeccionCaja
                titulo={tMetricas.fase7.resenasTitle}
                tituloColor="gray.700"
                descripcion={tMetricas.fase7.resenasHelp}
                muted={sub}
              >
                {(data.resumen.resenasBanda?.totalRecibidas ?? 0) > 0 ? (
                  <>
                    <HStack flexWrap="wrap" gap={2} mb={3} fontSize="sm">
                      <Text
                        as="span"
                        px={2}
                        py={1}
                        borderRadius="md"
                        bg="purple.50"
                        _dark={{ bg: 'whiteAlpha.200' }}
                        fontWeight="600"
                      >
                        Total: {data.resumen.resenasBanda?.totalRecibidas}
                      </Text>
                      <Text
                        as="span"
                        px={2}
                        py={1}
                        borderRadius="md"
                        bg="green.50"
                        _dark={{ bg: 'green.800' }}
                        fontWeight="600"
                      >
                        Publicadas: {data.resumen.resenasBanda?.publicadas}
                      </Text>
                      <Text
                        as="span"
                        px={2}
                        py={1}
                        borderRadius="md"
                        bg="orange.50"
                        _dark={{ bg: 'whiteAlpha.200' }}
                        fontWeight="600"
                      >
                        No publicadas: {data.resumen.resenasBanda?.sinPublicar}
                      </Text>
                    </HStack>
                    <Text fontSize="sm" fontWeight="700" mb={2} color="purple.500">
                      Medias (1–5)
                    </Text>
                    <VStack align="stretch" gap={2}>
                      {[
                        ['Comunicación', data.resumen.resenasBanda?.promedios?.comunicacion],
                        ['General', data.resumen.resenasBanda?.promedios?.general],
                        ['Puntualidad', data.resumen.resenasBanda?.promedios?.puntualidad],
                        ['Calidad artística', data.resumen.resenasBanda?.promedios?.calidadArtistica],
                      ].map(([label, v], i) => (
                        <MotionBox
                          key={label}
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.05 * i, duration: 0.3 }}
                        >
                          <Flex
                            justify="space-between"
                            borderRadius="lg"
                            px={3}
                            py={2}
                            bg="blackAlpha.50"
                            _dark={{ bg: 'whiteAlpha.100' }}
                            borderLeft="3px solid"
                            borderColor="purple.300"
                          >
                            <Text fontSize="sm" fontWeight="600">
                              {label}
                            </Text>
                            <Text fontSize="sm" fontWeight="800">
                              {v != null ? v : '—'}
                            </Text>
                          </Flex>
                        </MotionBox>
                      ))}
                    </VStack>
                  </>
                ) : (
                  <Text fontSize="sm" color={sub}>
                    {tMetricas.fase7.resenasEmpty}
                  </Text>
                )}
              </SeccionCaja>
              </SimpleGrid>
            </Card.Body>
          </Card.Root>
        </MotionBox>
      )}

      {data && data.comparativaAnterior && (
        <MotionBox
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Card.Root bg={cardBg} shadow="lg" borderWidth="1px" borderRadius="2xl" overflow="hidden">
          <Box h="2px" bgGradient="linear(to-r, purple.400, pink.200)" aria-hidden />
          <Card.Header pb={0} px={{ base: 4, md: 6 }} pt={5}>
            <Text fontWeight="800" fontSize="lg" letterSpacing="-0.02em">
              {tMetricas.informe.comparativaTitulo}
            </Text>
            <Text fontSize="sm" color={sub} fontWeight="normal" mt={1} lineHeight="tall">
              Periodo de referencia: {formatYmdEs(data.comparativaAnterior.rango.desdeYmd)} —{' '}
              {formatYmdEs(data.comparativaAnterior.rango.hastaYmd)} (misma longitud en días, hacia el pasado).
            </Text>
          </Card.Header>
          <Card.Body overflowX="auto" px={{ base: 4, md: 6 }} pb={6}>
            <Table.Root size="sm" variant="striped" colorPalette="purple" borderRadius="md" overflow="hidden">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Indicador</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign='end'>Periodo anterior</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign='end'>Periodo actual</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign='end'>Variación</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {Object.values(data.comparativaAnterior.kpi).map((d) => (
                  <Table.Row key={d.clave}>
                    <Table.Cell>{LABEL_DELTA_KPI[d.clave] || d.clave}</Table.Cell>
                    <Table.Cell textAlign='end'>{d.anterior}</Table.Cell>
                    <Table.Cell textAlign='end'>{d.actual}</Table.Cell>
                    <Table.Cell textAlign='end'>{formatDeltaLine(d)}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
            {data.comparativaAnterior.tasaCerrado.actual != null ||
            data.comparativaAnterior.tasaCerrado.anterior != null ? (
              <Text fontSize="sm" mt={4} color={sub}>
                Tasa «presente / cerrado» (asist. ensayos): anterior{' '}
                {formatPct(data.comparativaAnterior.tasaCerrado.anterior)} · actual{' '}
                {formatPct(data.comparativaAnterior.tasaCerrado.actual)}
                {data.comparativaAnterior.tasaCerrado.diffPuntos != null && (
                  <>
                    {' '}
                    (Δ {((data.comparativaAnterior.tasaCerrado.diffPuntos ?? 0) * 100).toFixed(1)} p. p.)
                  </>
                )}
              </Text>
            ) : null}
          </Card.Body>
        </Card.Root>
        </MotionBox>
      )}

      {data && data.resumen && data.ok && !data.vacio && Array.isArray(data.resumen.serieMensual) && (
        <MotionBox
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.05 }}
        >
        <Card.Root bg={cardBg} shadow="lg" borderWidth="1px" borderRadius="2xl" overflow="hidden">
          <Box h="2px" bgGradient="linear(to-r, cyan.400, purple.500)" aria-hidden />
          <Card.Header pb={0} px={{ base: 4, md: 6 }} pt={5}>
            <Text fontWeight="800" fontSize="lg" letterSpacing="-0.02em">
              {tMetricas.informe.evolucionTitulo}
            </Text>
            <Text fontSize="sm" color={sub} fontWeight="normal" mt={1} lineHeight="tall">
              Conteos por mes (mes civil UTC) y los músicos con más «Presente» a ensayos en el rango.
            </Text>
          </Card.Header>
          <Card.Body px={{ base: 4, md: 6 }} pb={6}>
            <SimpleGrid columns={{ base: 1, lg: 2 }} gap={6} alignItems="flex-start">
              <Box w="100%">
                <Text fontSize="sm" fontWeight="700" color="gray.600" mb={2}>
                  Por mes: ensayos y actuaciones
                </Text>
                {(() => {
                  const s = data.resumen.serieMensual;
                  const maxB = Math.max(1, ...s.map((x) => Math.max(x.ensayos, x.actuaciones)));
                  return s.length === 0 ? (
                    <Text fontSize="sm" color={sub}>
                      Sin meses en el rango.
                    </Text>
                  ) : (
                    <Table.Root size="sm" variant="simple">
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader>Mes</Table.ColumnHeader>
                          <Table.ColumnHeader textAlign='end'>Ens.</Table.ColumnHeader>
                          <Table.ColumnHeader textAlign='end'>Act.</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {s.map((fila) => (
                          <Table.Row key={fila.mes}>
                            <Table.Cell textTransform="capitalize">{fila.label}</Table.Cell>
                            <Table.Cell textAlign='end'>
                              <HStack justify="flex-end" gap={2}>
                                <Box flex={1} maxW="100px" minW="50px">
                                  <Progress.Root
                                    value={(fila.ensayos / maxB) * 100}
                                    size="sm"
                                    colorPalette="purple"
                                    borderRadius="md">
                                    <Progress.Track>
                                      <Progress.Range />
                                    </Progress.Track>
                                  </Progress.Root>
                                </Box>
                                {fila.ensayos}
                              </HStack>
                            </Table.Cell>
                            <Table.Cell textAlign='end'>
                              <HStack justify="flex-end" gap={2}>
                                <Box flex={1} maxW="100px" minW="50px">
                                  <Progress.Root
                                    value={(fila.actuaciones / maxB) * 100}
                                    size="sm"
                                    colorPalette="blue"
                                    borderRadius="md">
                                    <Progress.Track>
                                      <Progress.Range />
                                    </Progress.Track>
                                  </Progress.Root>
                                </Box>
                                {fila.actuaciones}
                              </HStack>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  );
                })()}
              </Box>
              <Box w="100%" overflowX="auto">
                <Text fontSize="sm" fontWeight="700" color="gray.600" mb={2}>
                  Ranking presentes a ensayos
                </Text>
                {(data.resumen.rankingPresentesEnsayos || []).length > 0 ? (
                  <Table.Root size="sm" variant="simple">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader>#</Table.ColumnHeader>
                        <Table.ColumnHeader>Músico</Table.ColumnHeader>
                        <Table.ColumnHeader textAlign='end'>Presente</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {data.resumen.rankingPresentesEnsayos.map((m) => (
                        <Table.Row key={m.musicoId}>
                          <Table.Cell>{m.posicion}</Table.Cell>
                          <Table.Cell>{nombreMusico(m.nombre, m.apellidos)}</Table.Cell>
                          <Table.Cell textAlign='end'>{m.presentes}</Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                ) : (
                  <Text fontSize="sm" color={sub}>
                    No hay registros de asistencia «Presente» a ensayos en este periodo.
                  </Text>
                )}
              </Box>
            </SimpleGrid>
            {data.resumen.pulidoFase7?.rankingTasaAsistenciaEnsayos && (
              <Box w="100%" mt={6} overflowX="auto">
                <Text fontSize="sm" fontWeight="700" color="gray.600" mb={1}>
                  {tMetricas.fase7.rankTasaTitle}
                </Text>
                <Text fontSize="xs" color={sub} mb={2}>
                  {tMetricas.fase7.rankTasaHelp}
                </Text>
                {(data.resumen.pulidoFase7.rankingTasaAsistenciaEnsayos || []).length > 0 ? (
                  <Table.Root size="sm" variant="simple">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader>#</Table.ColumnHeader>
                        <Table.ColumnHeader>Músico</Table.ColumnHeader>
                        <Table.ColumnHeader textAlign='end'>% asist.</Table.ColumnHeader>
                        <Table.ColumnHeader textAlign='end'>Presente</Table.ColumnHeader>
                        <Table.ColumnHeader textAlign='end'>Conv.</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {data.resumen.pulidoFase7.rankingTasaAsistenciaEnsayos.map((m) => (
                        <Table.Row key={m.musicoId}>
                          <Table.Cell>{m.posicion}</Table.Cell>
                          <Table.Cell>{nombreMusico(m.nombre, m.apellidos)}</Table.Cell>
                          <Table.Cell textAlign='end'>{formatPct(m.tasa)}</Table.Cell>
                          <Table.Cell textAlign='end'>{m.presentes}</Table.Cell>
                          <Table.Cell textAlign='end'>{m.convocatorias}</Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                ) : (
                  <Text fontSize="sm" color={sub}>
                    {tMetricas.fase7.rankTasaEmpty}
                  </Text>
                )}
              </Box>
            )}
          </Card.Body>
        </Card.Root>
        </MotionBox>
      )}

    </>
  );
}
